// Mirrored in each CRA app so they remain independently buildable.
// Only a non-secret session identifier is mirrored locally. The credential is
// an HttpOnly, host-only cookie held by the shared API, never by app JavaScript.
let configuration;
let stopWatching;
let refreshing = false;

export const browserHeaders = (token = null) => ({
  'X-Breck-Browser': '1',
  'X-Breck-Origin': window.location.origin,
  ...(token ? { 'X-Breck-Session': token } : {}),
});

export function sessionTokenForRequest(tokenKey) {
  // localStorage can change in another tab before this tab's old UI refreshes.
  // Bind requests to the account that this page actually rendered.
  return configuration?.tokenKey === tokenKey ? configuration.token : localStorage.getItem(tokenKey);
}

export function saveSession(tokenKey, token, user) {
  if (!token?.startsWith('browser:')) throw new Error('The shared sign-in service is unavailable. Please try again.');
  localStorage.setItem(tokenKey, token);
  if (tokenKey === 'token' && user?.username) localStorage.setItem('username', user.username);
  if (configuration?.tokenKey === tokenKey) configuration.token = token;
}

export function clearLocalSession(tokenKey) {
  localStorage.removeItem(tokenKey);
  if (tokenKey === 'token') localStorage.removeItem('username');
  if (configuration?.tokenKey === tokenKey) configuration.token = null;
}

async function fetchSession(apiUrl) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 10000);
  let response;
  try {
    response = await fetch(`${apiUrl}/auth/session`, {
      credentials: 'include', headers: browserHeaders(), cache: 'no-store', signal: controller.signal,
    });
  } finally { window.clearTimeout(timer); }
  if (response.status === 204) return null;
  if (!response.ok) throw new Error('Your Breck session could not be checked. Please try again.');
  const session = await response.json();
  if (!session.token?.startsWith('browser:') || !session.user) throw new Error('The shared sign-in service is unavailable. Please try again.');
  return session;
}

export async function restoreBrowserSession(tokenKey, apiUrl) {
  // Discard legacy per-app JWTs even when the session check is unavailable.
  if (!localStorage.getItem(tokenKey)?.startsWith('browser:')) clearLocalSession(tokenKey);
  const session = await fetchSession(apiUrl);
  if (session) saveSession(tokenKey, session.token, session.user);
  else clearLocalSession(tokenKey);
  return session;
}

export function refreshBrowserSession() {
  if (refreshing) return;
  refreshing = true;
  window.location.reload(); // Discard the previous account's state and caches.
}

export function watchBrowserSession(tokenKey, apiUrl, reload = refreshBrowserSession) {
  stopWatching?.();
  configuration = { tokenKey, token: localStorage.getItem(tokenKey), apiUrl };
  let checking = false, stopped = false;
  const sync = async () => {
    if (checking || stopped) return;
    checking = true;
    const expected = configuration.token;
    try {
      const session = await fetchSession(apiUrl);
      if (!stopped && configuration.token === expected && (session?.token || null) !== expected) reload();
    } catch { /* Network failures never manufacture a logout. Retry on focus. */ }
    finally { checking = false; }
  };
  const timer = window.setInterval(sync, 10000);
  window.addEventListener('focus', sync);
  window.addEventListener('pageshow', sync);
  document.addEventListener('visibilitychange', sync);
  stopWatching = () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', sync);
    window.removeEventListener('pageshow', sync);
    document.removeEventListener('visibilitychange', sync);
    configuration = undefined;
    stopWatching = undefined;
  };
  return stopWatching;
}

export async function signOutBrowser(tokenKey, apiUrl) {
  const token = sessionTokenForRequest(tokenKey);
  const response = await fetch(`${apiUrl}/auth/logout`, {
    method: 'POST', credentials: 'include', headers: browserHeaders(token),
  });
  if (response.status === 409) { refreshBrowserSession(); throw new Error('Your Breck login changed. Reloading…'); }
  if (!response.ok) throw new Error('Could not sign out. Check your connection and try again.');
  clearLocalSession(tokenKey);
}
